import React from 'react';

import NumberFormat from 'CommonComponents/LocalisedNumberFormat';

/**
 * The in-place approval-chain editor for one rule.
 *
 * Every level owns the amount that decides when it applies, and the amounts live
 * in two different places because they mean two different things. Level 1 carries
 * the rule's own amount: changes at or below it need no approval at all. Levels
 * above it carry escalation values — `levels` holds the approvers in order with
 * their stored pivot values, and the engine ends its chain at the approver whose
 * value covers the change, so the value stored against approver k is the amount
 * above which the change escalates to level k+1. That is why the input on level
 * k+1 edits `levels[k].value` and the top level always stores null.
 *
 * A Yes / No rule approves every change it catches, so the engine ignores all of
 * these amounts and none of the inputs are shown. Stored values are left intact
 * so switching back to Value returns the chain as it was.
 */
export default function ApproverLevelsPanel({ scopeName, levels, users, approvalType, threshold, readOnly, onChange, onThresholdChange }) {
    const byValue = approvalType === 'value';
    const lastIndex = levels.length - 1;

    const setLevel = (index, changes) => onChange(levels.map((level, i) => (i === index ? { ...level, ...changes } : level)));

    const removeLevel = (index) => {
        const next = levels.filter((level, i) => i !== index);

        // Whichever level ends up on top approves whatever reaches it.
        return onChange(next.map((level, i) => (i === next.length - 1 ? { ...level, value: null } : level)));
    };

    const addLevel = () => {
        const highest = levels.reduce((carry, level) => Math.max(carry, Number(level.value) || 0), Number(threshold) || 0);

        // The level that was on top now escalates, so it needs a ceiling; without one
        // the engine would stop there and never reach the level being added.
        const next = levels.map((level, i) => (i === lastIndex ? { ...level, value: highest + 1 } : level));

        onChange([...next, { user_id: '', value: null }]);
    };

    const escalatesAbove = (index) => (index === 0 ? null : levels[index - 1]?.value);

    // react-number-format re-emits its value whenever a prop changes it, not only
    // on typing, so every handler compares against what it was given before
    // propagating — otherwise the panel would mark the row dirty on first render.
    const asText = (value) => (value === null || value === undefined ? '' : String(value));

    const amountInput = (value, label, onValue) => (
        <NumberFormat
            className="bc-input tw-w-[110px]"
            value={asText(value)}
            decimalScale={2}
            thousandSeparator={true}
            inputMode="decimal"
            allowNegative={false}
            disabled={readOnly}
            aria-label={label}
            onValueChange={({ value: next }) => next !== asText(value) && onValue(next === '' ? null : next)}
        />
    );

    // Level 1 owns the rule's own amount; every level above it owns the floor it
    // escalates at. Neither applies to a Yes / No rule, which approves everything.
    const condition = (index) => {
        if (!byValue) {
            return (
                <span className="bc-level__cond">
                    {index === 0 ? $t('Approves every change this rule covers.') : $t('Also required on every change this rule covers.')}
                </span>
            );
        }

        if (index === 0) {
            return (
                <span className="bc-level__cond">
                    {$t('Required when the change exceeds')}
                    {amountInput(threshold, $t('Approval amount'), onThresholdChange)}
                </span>
            );
        }

        return (
            <span className="bc-level__cond">
                {$t('Also required when the change exceeds')}
                {amountInput(escalatesAbove(index), $t('Escalation value'), (next) => setLevel(index - 1, { value: next }))}
            </span>
        );
    };

    return (
        <div className="bc-chain">
            <h5 className="bc-chain__title">{$t('Approval levels — {{scope}}', { scope: scopeName })}</h5>

            {levels.map((level, index) => (
                <div key={`level-${index}`} className="bc-level">
                    <span className="bc-level__badge">{$t('Level {{level}}', { level: index + 1 })}</span>

                    <select
                        className="bc-select"
                        value={level.user_id || ''}
                        disabled={readOnly}
                        aria-label={$t('Approver')}
                        onChange={(e) => setLevel(index, { user_id: e.target.value ? Number(e.target.value) : '' })}
                    >
                        <option value="">{$t('Select approver')}</option>
                        {users.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.name}
                            </option>
                        ))}
                    </select>

                    {condition(index)}

                    {!readOnly && (
                        <button type="button" className="bc-level__remove" onClick={() => removeLevel(index)}>
                            {$t('Remove')}
                        </button>
                    )}
                </div>
            ))}

            {!readOnly && (
                <button type="button" className="bc-addlevel" onClick={addLevel}>
                    {$t('+ Add approval level')}
                </button>
            )}

            <p className="bc-help tw-mt-2">
                {byValue
                    ? $t('Levels approve in order. A change routes to every level whose amount it exceeds; all must approve before the change is applied.')
                    : $t('Levels approve in order. Every level must approve before the change is applied.')}
            </p>
        </div>
    );
}
